// Angular
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import {
  FormGroup,
  FormsModule,
  ReactiveFormsModule
} from '@angular/forms';
import { Router } from '@angular/router';

// Angular Material
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatNativeDateModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';

// Third party
import * as _ from 'lodash';
import moment from 'moment';
import { Subscription } from 'rxjs';

// Local library
import {
  CountdownPipe,
  DateFormatPipe,
  GetEventStatusPipe,
  GetRaceStatusPipe,
  GetTrackTypePipe,
  NumberWithCommasPipe,
  SortEventsPipe,
  UnsubscribeObservableService,
TranslatePipe
} from '@amwager/util';
import {
  HORSE_BREED,
  IGetEventsDetailsResponse
} from '@amwager/util-ts';
import { SessionService } from '@amwager/data-access';
import { ProfileComponent } from '@amwager/account/feature-mat';
import { ProfileService } from '@amwager/account/data-access';
import {
  IReplayRow,
  IReplays,
  ReplaysAndSearchService,
  WagerFacade,
  WagerService
} from '@amwager/wager/data-access';
import { takeUntil } from 'rxjs/operators';

// Local file
import { ReplayVideoComponent } from '../replay-video/replay-video.component';

/**
 * Replays and Search page — filters replays and opens their videos
 */
@Component({
  selector: 'feature-mat-replays-and-search',
  templateUrl: './replays-and-search.component.html',
  styleUrls: ['./replays-and-search.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatNativeDateModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    MatAutocompleteModule,
    DateFormatPipe,
    NumberWithCommasPipe,
    SortEventsPipe,
    GetTrackTypePipe,
    GetRaceStatusPipe,
    CountdownPipe,
    GetEventStatusPipe,
    ProfileComponent,
    TranslatePipe
  ],
  providers: [SortEventsPipe]
})
export class ReplaysAndSearchComponent extends UnsubscribeObservableService
  implements OnInit, OnDestroy {

  @ViewChild('favorites') favoritesRef!: ElementRef;

  readonly HORSE_BREED = HORSE_BREED;
  readonly maxDate = new Date();

  getReplaysForm!: FormGroup;

  /** Full replays list from the store, flattened + sorted */
  allReplays: IReplayRow[] = [];

  /** Currently-rendered rows (allReplays after client-side track filter) */
  replays: IReplayRow[] = [];

  /** Track names derived from the current replays list */
  trackNames: string[] = [];

  /** Selected track filter (client-side) */
  track: string = '';

  /** Runner autocomplete state */
  runnersName: string[] = [];
  headToHeadName: string[] = [];
  isLoadingRunner = false;
  isLoadingHead = false;

  /** True while a replays fetch is in flight (nav, filter change, refetch) */
  isLoadingReplays = true;

  /** Flips true after the first settled replays emission */
  hasEverReceivedReplaysEmission = false;

  /**
   * True whenever a replays fetch is in flight — initial nav,
   * date/breed change, or any user-triggered refetch. Flips
   * false on the settled response (success, fail or empty),
   * mirroring the wager / results skeleton lifecycle.
   * @returns {boolean} true = show full-page skeleton
   */
  get showInitialSkeleton(): boolean {
    return this.isLoadingReplays;
  }

  /** Favorites row state — mirrors ResultsComponent */
  userEmail?: string;
  setListFavorites!: Set<string>;
  listOfFavoriteEvents: IGetEventsDetailsResponse[] = [];
  selectedEvent?: IGetEventsDetailsResponse;
  sortBy: string = 'Name';
  eventTypeFilter: string[] = [
    'Thoroughbred',
    'Quarterhorse',
    'Greyhound',
    'Harness',
    'International'
  ];
  events: IGetEventsDetailsResponse[] = [];

  // True when inline profile panel is open — hides main replays content
  isProfileOpen: boolean = false;

  private replayDialogRef?: MatDialogRef<ReplayVideoComponent>;
  private replayPending: boolean = false;
  private runnersSub?: Subscription;

  get formVal() { return this.getReplaysForm?.controls; }

  constructor(
    private readonly wagerFacade: WagerFacade,
    private readonly wagerService: WagerService,
    private readonly replaysAndSearchService: ReplaysAndSearchService,
    private readonly sessionService: SessionService,
    private readonly dialog: MatDialog,
    private readonly router: Router,
    private readonly cdRef: ChangeDetectorRef,
    private readonly profileService: ProfileService
  ) {
    super();
  }

  ngOnInit(): void {
    this.userEmail = this.sessionService.getUserEmail();
    this.getReplaysForm = this.replaysAndSearchService.buildForm();

    this.observeReplays();
    this.observeRunners();
    this.observeReplayVideo();
    this.observeEventsAndFavorites();

    // Mirror the profile panel visibility from the shared service
    this.profileService.showProfile$
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((show: boolean) => {
        this.isProfileOpen = show;
        this.cdRef.markForCheck();
      });

    // Kick off initial fetch with today's date + all breeds.
    this.replaysAndSearchService.fetchReplaysImmediate(
      'ReplaysAndSearchComponent:ngOnInit',
      this.getReplaysForm
    );
  }

  override ngOnDestroy(): void {
    this.toUnsubscribe();
    this.runnersSub?.unsubscribe();
    this.replaysAndSearchService.clearTimers();
    if (this.replayDialogRef) this.replayDialogRef.close();
  }

  /**
   * Subscribe to the replays store selector and hydrate table state
   * @returns {void}
   */
  private observeReplays(): void {
    this.wagerFacade.getReplays$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((replays?: IReplays) => {
        const { rows, trackNames } = this.replaysAndSearchService.normalizeReplays(replays);

        this.allReplays = rows;
        this.replays = this.replaysAndSearchService.filterByTrack(rows, this.track);
        this.trackNames = trackNames;

        // Settle skeleton only on a completed cycle (success, fail or empty);
        // ignore the sync-emit and in-flight (isProcessing:true) emissions.
        if (replays?.isProcessing === false) {
          this.isLoadingReplays = false;
          this.hasEverReceivedReplaysEmission = true;
        }

        this.cdRef.markForCheck();
      });
  }

  /**
   * Subscribe to the runners store selector for autocomplete lists
   * @returns {void}
   */
  private observeRunners(): void {
    this.runnersSub = this.wagerFacade.getRunners$()
      .subscribe((runners?: string[]) => {
        this.isLoadingRunner = false;
        this.isLoadingHead = false;

        if (this.activeLookup === 'runner') this.runnersName = runners || [];
        else if (this.activeLookup === 'head') this.headToHeadName = runners || [];

        this.cdRef.markForCheck();
      });
  }

  /** Which input triggered the most recent runners lookup */
  private activeLookup: 'runner' | 'head' | null = null;

  /**
   * Wire favorites (from account preferences) + events (from store).
   * Mirrors the pattern used by ResultsComponent.
   * @returns {void}
   */
  private observeEventsAndFavorites(): void {
    this.wagerFacade.getSelectAllEventTrackRace$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((allEvents?: IGetEventsDetailsResponse[]) => {
        this.events = allEvents || [];
        this.rebuildFavoriteEvents();
        this.cdRef.markForCheck();
      });

    this.wagerService.observerPreferenceFavorites()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((listFavorites?: string[]) => {
        this.setListFavorites = new Set(listFavorites || []);
        this.rebuildFavoriteEvents();
        this.cdRef.markForCheck();
      });
  }

  /**
   * Intersect account favorites with the current events list
   * @returns {void}
   */
  private rebuildFavoriteEvents(): void {
    if (!this.setListFavorites || this.setListFavorites.size === 0) {
      this.listOfFavoriteEvents = [];
      return;
    }

    this.listOfFavoriteEvents = _.filter(
      this.events,
      (event: IGetEventsDetailsResponse) => this.setListFavorites.has(event?.id as string)
    );
  }

  /**
   * Open the replay dialog when a replay video URL lands in the store
   * @returns {void}
   */
  private observeReplayVideo(): void {
    this.wagerFacade.getRaceResult$()
      .pipe(takeUntil(this.toUnsubscribe$))
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .subscribe((result?: any) => {
        const replayVideo: string | undefined = result?.replayVideo;
        if (!replayVideo || !this.replayPending) return;

        this.replayPending = false;

        // min-height keeps the title, video and runners table
        // visible; it still grows if the content is taller.
        this.replayDialogRef = this.dialog.open(ReplayVideoComponent, {
          data: replayVideo,
          width: '500px',
          minHeight: '600px',
          hasBackdrop: false,
          disableClose: false,
          panelClass: 'replay-video-dialog'
        });

        this.replayDialogRef.afterClosed().subscribe(() => {
          this.replayDialogRef = undefined;
        });
      });
  }

  /**
   * Date picker change — refetch replays for the picked day.
   * Stores a real `Date` so the datepicker keeps the value on screen.
   * @param {any} event mat datepicker change event
   * @returns {void}
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setDate(event: any): void {
    const rawValue = event?.value ?? event?.target?.value;
    if (!rawValue) return;

    const parsed = moment(rawValue);
    if (!parsed.isValid()) return;

    this.formVal['dateBy']?.setValue(parsed.toDate());
    this.resetTrackFilter();
    this.search();
  }

  /**
   * Reset filter to today
   * @returns {void}
   */
  todayDate(): void {
    this.formVal['dateBy']?.setValue(new Date());
    this.resetTrackFilter();
    this.search();
  }

  /**
   * Breed selection change — refetch replays for the picked breed
   * @param {any} event mat-select change event
   * @returns {void}
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selectBreed(event: any): void {
    const value = event?.value;
    this.formVal['breed']?.setValue(value);
    this.resetTrackFilter();
    this.search();
  }

  /**
   * Client-side track filter — narrows the visible rows
   * @param {any} event mat-select change event
   * @returns {void}
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selectTrack(event: any): void {
    const value = (event?.value || '') as string;
    this.track = value;
    this.replays = this.replaysAndSearchService.filterByTrack(this.allReplays, value);
    this.cdRef.markForCheck();
  }

  /**
   * Runner-name input handler — debounced lookup + list toggling
   * @param {'runner' | 'head'} type which input fired
   * @param {any} event input event
   * @returns {void}
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  searchName(type: 'runner' | 'head', event: any): void {
    const value = event?.target?.value || '';

    this.activeLookup = type;

    if (type === 'runner') this.formVal['runnerName']?.setValue(value);
    else this.formVal['headToHead']?.setValue(value);

    const scheduled = this.replaysAndSearchService.lookupRunners(value, () => {
      if (type === 'runner') {
        this.isLoadingRunner = false;
        this.runnersName = [];
      } else {
        this.isLoadingHead = false;
        this.headToHeadName = [];
      }
    });

    if (scheduled) {
      if (type === 'runner') this.isLoadingRunner = true;
      else this.isLoadingHead = true;
    }

    this.cdRef.markForCheck();
  }

  /**
   * Autocomplete option selected — commit and refetch
   * @param {string} name selected runner name
   * @param {'runner' | 'head'} type which input to populate
   * @returns {void}
   */
  selectName(name: string, type: 'runner' | 'head'): void {
    if (type === 'runner') this.formVal['runnerName']?.setValue(name);
    else this.formVal['headToHead']?.setValue(name);
    this.search();
  }

  /**
   * Clear a runner-name input and refetch
   * @param {'runner' | 'head'} type which input to clear
   * @returns {void}
   */
  clearRunner(type: 'runner' | 'head'): void {
    if (type === 'runner') {
      this.formVal['runnerName']?.setValue('');
      this.runnersName = [];
    } else {
      this.formVal['headToHead']?.setValue('');
      this.headToHeadName = [];
    }
    this.search();
  }

  /**
   * Debounced replays refetch triggered by any filter change
   * @returns {void}
   */
  search(): void {
    this.isLoadingReplays = true;
    this.cdRef.markForCheck();

    this.replaysAndSearchService.fetchReplaysDebounced(
      'ReplaysAndSearchComponent:search',
      this.getReplaysForm
    );
  }

  /**
   * Reset filters to defaults and refetch
   * @returns {void}
   */
  clear(): void {
    this.replaysAndSearchService.resetForm(this.getReplaysForm);
    this.resetTrackFilter();
    this.runnersName = [];
    this.headToHeadName = [];

    this.isLoadingReplays = true;
    this.cdRef.markForCheck();

    setTimeout(() => {
      this.replaysAndSearchService.fetchReplaysImmediate(
        'ReplaysAndSearchComponent:clear',
        this.getReplaysForm
      );
    }, 100);
  }

  /**
   * Kick off replay-video fetch for a table row
   * @param {IReplayRow} replay clicked row
   * @returns {void}
   */
  getReplayVideo(replay: IReplayRow): void {
    if (!replay) return;

    this.replayPending = true;
    this.replaysAndSearchService.playReplay(
      replay,
      'ReplaysAndSearchComponent:getReplayVideo'
    );
  }

  /**
   * Favorites chip — redirect to the wager page with this event track
   * (and its current race) pre-selected.
   * @param {IGetEventsDetailsResponse} favorite selected event
   * @returns {void}
   */
  selectFavorite(favorite: IGetEventsDetailsResponse): void {
    if (!favorite) return;

    this.selectedEvent = favorite;
    // OLD: jumped to the results page with only the event pre-selected.
    // this.router.navigate(['/results'], {
    //   state: {
    //     eventId: favorite?.id,
    //     runId: favorite?.ToteResponse?.RunId
    //   }
    // });
    // NEW: redirect to wager with the event track + current race pre-selected.
    this.router.navigate(['/wager'], {
      state: {
        eventId: favorite?.id,
        runId: favorite?.ToteResponse?.RunId,
        raceId: favorite?.ToteResponse?.EventDetail?.currentRace?.RaceId,
      }
    });
  }

  /**
   * Horizontal scroll for the favorites row
   * @param {'prev' | 'next'} direction scroll direction
   * @returns {void}
   */
  scrollTo(direction: 'prev' | 'next'): void {
    const container = this.favoritesRef?.nativeElement;
    if (!container) return;

    const scrollAmount = 200;
    if (direction === 'prev') container.scrollLeft -= scrollAmount;
    else container.scrollLeft += scrollAmount;
  }

  /**
   * `*ngFor` trackBy for replay rows
   * @param {number} index list index
   * @param {IReplayRow} item row
   * @returns {string} stable row key
   */
  trackReplayFn(index: number, item: IReplayRow): string {
    return `${item?.trackId}-${item?.race}-${item?.date}-${index}`;
  }

  /**
   * `*ngFor` trackBy for favorite events
   * @param {number} _index list index (unused)
   * @param {any} item event
   * @returns {string} stable event id
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  trackFavoriteFn(_index: number, item: any): string {
    return item?.id;
  }

  /**
   * `*ngFor` trackBy for HORSE_BREED keyvalue entries
   * @param {number} _index list index (unused)
   * @param {any} item keyvalue entry
   * @returns {any} entry key
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  trackBreedFn(_index: number, item: any): any {
    return item?.key;
  }

  /**
   * `*ngFor` trackBy for track name strings
   * @param {number} _index list index (unused)
   * @param {string} item track name
   * @returns {string} track name
   */
  trackTrackFn(_index: number, item: string): string {
    return item;
  }

  /**
   * Reset the client-side track filter after a new fetch is triggered
   * @returns {void}
   */
  private resetTrackFilter(): void {
    this.trackNames = [];
    this.track = '';
  }
}
